import { CardType } from '@framework/analytics/models/card-model';

/**
 * Grid column count for the report/dashboard GridStack layout.
 * Must match `GridStackConfigService.defaultOptions.column`.
 */
export const CARD_GRID_COLUMNS = 24;

/** Row step in pixels — must match `GridStackConfigService.defaultOptions.cellHeight`. */
export const CARD_GRID_ROW_HEIGHT_PX = 10;

export interface CardGridSize {
	/** Resize floor — smallest allowed width in grid columns. */
	minW: number;
	/** Resize floor — smallest allowed height in grid rows. */
	minH: number;
	/** Starting width when a card is first placed on the grid. */
	defaultW: number;
	/** Starting height when a card is first placed on the grid. */
	defaultH: number;
}

type CardGridSizeSpec = CardGridSize;

const FALLBACK_SIZE: CardGridSizeSpec = {
	minW: 4,
	minH: 6,
	defaultW: 8,
	defaultH: 12,
};

/**
 * Per-type resize floors and ideal starting dimensions for a 24-column grid.
 * `minW`/`minH` guard legibility; `defaultW`/`defaultH` are the sizes new cards
 * are created with (always >= the corresponding minimum).
 */
const SIZE_BY_CARD_TYPE: Record<CardType, CardGridSizeSpec> = {
	[CardType.Chart]: {
		minW: 2,
		minH: 4,
		// Full width / taller plot — prior 12×20 was too small for line charts (JEP-10153).
		defaultW: 24,
		defaultH: 32,
	},
	[CardType.Table]: {
		minW: 2,
		minH: 4,
		defaultW: 24,
		defaultH: 32,
	},
	[CardType.Markdown]: {
		minW: 1,
		minH: 1,
		defaultW: 12,
		defaultH: 20,
	},
	[CardType.Html]: {
		minW: 1,
		minH: 1,
		defaultW: 12,
		defaultH: 20,
	},
	[CardType.Image]: {
		minW: 1,
		minH: 1,
		defaultW: 12,
		defaultH: 12,
	},
	[CardType.Filter]: {
		minW: 2,
		minH: 4,
		defaultW: 8,
		defaultH: 10,
	},
	[CardType.Map]: {
		minW: 6,
		minH: 6,
		defaultW: 12,
		defaultH: 20,
	},
	[CardType.Analysis]: {
		minW: 6,
		minH: 10,
		defaultW: 12,
		defaultH: 20,
	},
};

/**
 * Resolves resize floors and default placement size for a card type.
 * Used when creating cards, hydrating layouts, and configuring GridStack widgets.
 */
export function getCardGridSize(cardType: CardType): CardGridSize {
	return SIZE_BY_CARD_TYPE[cardType] ?? FALLBACK_SIZE;
}
